<script lang="ts">
	import type { RepeaterResult } from '$lib/types/repeater';
	import { createEventDispatcher } from 'svelte';

	export let repeater: RepeaterResult;
	export let showModal = false;

	const dispatch = createEventDispatcher();

	function close() {
		showModal = false;
		dispatch('close');
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			close();
		}
	}

	function getAdditionalInfo(repeater: RepeaterResult) {
		const excludeFields = [
			'Callsign',
			'Frequency',
			'Input Freq',
			'PL',
			'TSQ',
			'Landmark',
			'Use',
			'Operational Status',
			'DMR Color Code',
			'State ID',
			'Rptr ID'
		];
		const excludeValues = ['', '0', 'No'];

		return Object.entries(repeater)
			.filter(
				([key, value]) =>
					!excludeFields.includes(key) && !excludeValues.includes(value?.toString() || '')
			)
			.reduce(
				(acc, [key, value]) => {
					acc[key] = value;
					return acc;
				},
				{} as Record<string, string | undefined>
			);
	}

	function getRepeaterBookUrl(repeater: RepeaterResult) {
		const stateId = repeater['State ID'];
		const rptrId = repeater['Rptr ID'];
		return `https://www.repeaterbook.com/repeaters/details.php?state_id=${stateId}&ID=${rptrId}`;
	}
</script>

{#if showModal}
	<div on:keydown={handleKeydown} role="presentation">
		<dialog
			class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none"
			aria-labelledby="modal-title"
			open={showModal}
		>
			<button
				type="button"
				class="fixed inset-0 bg-black opacity-50"
				on:click={close}
				aria-label="Close modal background overlay"
			></button>
			<div class="relative mx-auto my-6 w-auto max-w-3xl rounded-lg bg-white p-6 dark:bg-gray-800">
				<!-- Header -->
				<div class="mb-4 flex items-center justify-between border-b pb-4">
					<h3 id="modal-title" class="text-xl font-semibold text-gray-900 dark:text-white">
						{repeater.Callsign} Additional Information
					</h3>
					<button
						type="button"
						class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
						on:click={close}
						aria-label="Close modal"
					>
						<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							></path>
						</svg>
					</button>
				</div>

				<!-- Content -->
				<div class="max-h-[70vh] overflow-y-auto">
					<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
						{#each Object.entries(getAdditionalInfo(repeater)) as [key, value]}
							<div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
								<div class="text-sm font-medium text-gray-500 dark:text-gray-400">
									{key}
								</div>
								<div class="mt-1 text-base text-gray-900 dark:text-white">
									{value || '-'}
								</div>
							</div>
						{/each}
					</div>
				</div>

				<!-- Footer -->
				<div class="mt-6 flex flex-col gap-4 border-t pt-4">
					<div class="flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-400">
						<p>
							<a
								href={getRepeaterBookUrl(repeater)}
								target="_blank"
								rel="noopener noreferrer"
								class="font-medium text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
								>View on RepeaterBook</a
							>
							for additional information and details.
						</p>
						<p>
							Notice an error? Please submit updates through
							<a
								href={getRepeaterBookUrl(repeater)}
								target="_blank"
								rel="noopener noreferrer"
								class="font-medium text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
								>RepeaterBook's update system</a
							>.
						</p>
					</div>
					<div class="flex items-center justify-end">
						<button
							type="button"
							class="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
							on:click={close}
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</dialog>
	</div>
{/if}

<style>
	dialog {
		border: none;
		background: transparent;
	}

	dialog::backdrop {
		background: transparent;
	}
</style>
